import {
  Icon,
  ICON_NAMES,
  Menu,
  MENU_ITEM_SELECTED_INDICATORS,
  MenuItem,
} from '@rgrd/component-library'
import { Link, useLocation } from 'react-router'

import { useActiveApp } from '~/shell/app/useActiveApp'
import { useEnabledProducts } from '~/shell/auth/useEnabledProducts'

import styles from './ProductSwitcher.module.css'

/**
 * The product switcher.
 *
 * Lists the sibling products a session may reach, the current one carrying the checkmark. Every
 * product is an app this suite serves, so each item is a same-origin route that keeps the active
 * customer, navigated in-app through a react-router `Link` rather than a full-page load. When only
 * one product is offered there is nothing to switch to, so its name is shown as plain text instead.
 */
export const ProductSwitcher = () => {
  const { pathname } = useLocation()
  const activeApp = useActiveApp()
  const products = useEnabledProducts(pathname, activeApp)

  if (products.length === 0) {
    return null
  }
  if (products.length === 1) {
    return <span className={styles.soleProduct}>{products[0].name}</span>
  }

  const currentProduct = products.find((product) => product.current)
  const triggerLabel = currentProduct?.name ?? products[0].name

  return (
    <Menu
      label="Switch product"
      align="start"
      triggerClassName={styles.trigger}
      trigger={() => (
        <>
          <span className={styles.triggerLabel}>{triggerLabel}</span>
          <Icon name={ICON_NAMES.ARROWS_CHEVRON_DOWN} size={12} color="grey" />
        </>
      )}
    >
      {products.map((product) => (
        // Every product lives on this origin, so each item navigates in-app through a react-router
        // Link rather than the MenuItem's default full-page `<a href>`, whether or not it is the one
        // being viewed.
        <MenuItem
          key={product.id}
          to={product.href}
          description={product.description}
          selected={product.current}
          selectedIndicator={MENU_ITEM_SELECTED_INDICATORS.check}
          renderItem={({ children, ...anchorProps }) => (
            <Link {...anchorProps} to={product.href}>
              {children}
            </Link>
          )}
        >
          {product.name}
        </MenuItem>
      ))}
    </Menu>
  )
}
